import type { Dispatch, ReactNode, SetStateAction } from 'react'
import type { ChatMessage, ChatSide } from '@/components/generals/chat/chat-card'
import type { InquirySource } from '@/types'

import { useCallback, useEffect, useRef, useState } from 'react'
import { isValidEmail } from '@/lib/format'
import { submitInquiry } from '@/lib/inquiries'
import { useReducedMotion } from '@/hooks/use-reduced-motion'

export interface ScriptLine {
    side: ChatSide
    text?: string
    content?: ReactNode
}

interface ChatScriptOptions {
    script: Array<ScriptLine>
    playKey: string | number
    enabled: boolean
    onComplete?: () => void
    startDelay?: number
}

interface ChatScriptState {
    messages: Array<ChatMessage>
    setMessages: Dispatch<SetStateAction<Array<ChatMessage>>>
    typing: ChatSide | null
    setTyping: Dispatch<SetStateAction<ChatSide | null>>
}

/**
 * Plays a scripted conversation one message at a time, each preceded by a
 * short typing moment. Replays whenever `playKey` changes. Turning `enabled`
 * off stops playback but keeps whatever is already on screen. With reduced
 * motion the whole conversation appears at once.
 */
export const useChatScript = ({
    script,
    playKey,
    enabled,
    onComplete,
    startDelay = 450
}: ChatScriptOptions): ChatScriptState => {
    const reduced = useReducedMotion()
    const [messages, setMessages] = useState<Array<ChatMessage>>([])
    const [typing, setTyping] = useState<ChatSide | null>(null)
    const scriptRef = useRef(script)
    const completeRef = useRef(onComplete)

    useEffect(() => {
        scriptRef.current = script
        completeRef.current = onComplete
    })

    useEffect(() => {
        if (!enabled) return

        let cancelled = false
        const timers: Array<number> = []
        const lines = scriptRef.current

        const wait = (milliseconds: number) => new Promise<void>(resolve => {
            timers.push(window.setTimeout(resolve, milliseconds))
        })

        const toMessage = (line: ScriptLine, index: number): ChatMessage => ({
            id: `script-${playKey}-${index}`,
            side: line.side,
            text: line.text,
            content: line.content
        })

        const play = async () => {
            setTyping(null)

            if (reduced) {
                setMessages(lines.map(toMessage))
                completeRef.current?.()
                return
            }

            setMessages([])
            await wait(startDelay)

            for (let index = 0; index < lines.length; index++) {
                if (cancelled) return

                const line = lines[index]
                const length = line.text?.length ?? 90

                setTyping(line.side)
                await wait(Math.min(1700, 700 + length * 11))

                if (cancelled) return

                setTyping(null)
                setMessages(previous => [...previous, toMessage(line, index)])
                await wait(line.side === 'left' ? 1050 : 800)
            }

            if (!cancelled) completeRef.current?.()
        }

        void play()

        return () => {
            cancelled = true
            timers.forEach(timer => window.clearTimeout(timer))
            setTyping(null)
        }
    }, [playKey, enabled, reduced, startDelay])

    return { messages, setMessages, typing, setTyping }
}

export type VisitorStage = 'idle' | 'replying' | 'email' | 'sending' | 'done'

interface VisitorChatOptions {
    source: InquirySource
    topic?: string
    firstReply: string
    followUpReply: string
    confirmation: (email: string) => string
    setMessages: Dispatch<SetStateAction<Array<ChatMessage>>>
    setTyping: Dispatch<SetStateAction<ChatSide | null>>
    onStart?: () => void
}

interface VisitorChatState {
    stage: VisitorStage
    draft: string
    setDraft: (value: string) => void
    email: string
    setEmail: (value: string) => void
    emailError: string | null
    sendMessage: () => void
    submitEmail: () => Promise<void>
}

/**
 * Lets a visitor talk into a scripted chat card: their message appears as a
 * bubble, the team "types", replies, and then asks for an email. The email
 * and everything the visitor wrote are sent to the inquiry route.
 */
export const useVisitorChat = ({
    source,
    topic,
    firstReply,
    followUpReply,
    confirmation,
    setMessages,
    setTyping,
    onStart
}: VisitorChatOptions): VisitorChatState => {
    const reduced = useReducedMotion()
    const [stage, setStage] = useState<VisitorStage>('idle')
    const [draft, setDraft] = useState('')
    const [email, setEmail] = useState('')
    const [emailError, setEmailError] = useState<string | null>(null)
    const notes = useRef<Array<string>>([])
    const counter = useRef(0)
    const timers = useRef<Array<number>>([])

    useEffect(() => {
        const pending = timers.current

        return () => pending.forEach(timer => window.clearTimeout(timer))
    }, [])

    const later = useCallback((callback: () => void, milliseconds: number) => {
        timers.current.push(window.setTimeout(callback, reduced ? 0 : milliseconds))
    }, [reduced])

    const push = useCallback((side: ChatSide, text: string) => {
        counter.current += 1

        const id = `visitor-${counter.current}`

        setMessages(previous => [...previous, { id, side, text }])
    }, [setMessages])

    const sendMessage = () => {
        const text = draft.trim()

        if (!text || stage === 'replying' || stage === 'sending' || stage === 'done') return

        const first = stage === 'idle'

        onStart?.()
        notes.current.push(text)
        setDraft('')
        push('right', text)
        setStage('replying')

        later(() => setTyping('left'), 350)
        later(() => {
            setTyping(null)
            push('left', first ? firstReply : followUpReply)
            setStage('email')
        }, first ? 2000 : 1300)
    }

    const submitEmail = async () => {
        const value = email.trim()

        if (!isValidEmail(value)) {
            setEmailError('Please enter a valid work email.')
            return
        }

        setEmailError(null)
        setStage('sending')
        push('right', value)
        setTyping('left')

        try {
            await submitInquiry({
                source,
                email: value,
                topic,
                message: notes.current.join('\n\n')
            })

            later(() => {
                setTyping(null)
                push('left', confirmation(value))
                setStage('done')
            }, 700)
        }
        catch {
            setTyping(null)
            push('left', "That didn't go through just now. Please try again in a moment.")
            setStage('email')
        }
    }

    return {
        stage,
        draft,
        setDraft,
        email,
        setEmail,
        emailError,
        sendMessage,
        submitEmail
    }
}
